
import { TrainingFormValues, TrainingSession, SessionType } from '../types';
// import { format, parse } from 'date-fns';

// Updated function to handle DATE type conversion
export const getFormValuesFromSession = (session: TrainingSession): TrainingFormValues | null => {
  try {
    // Ensure date is always a Date object
    let sessionDate: Date;
    
    // Use type checking instead of instanceof for more type safety
    if (session.date && typeof session.date === 'object' && 
        Object.prototype.toString.call(session.date) === '[object Date]') {
      sessionDate = session.date as Date;
    } else if (typeof session.date === 'string') {
      // Try parsing different date formats
      try {
        // First try parsing as ISO date
        sessionDate = new Date(session.date);
        
        // If that fails, try more specific parsing
        if (isNaN(sessionDate.getTime())) {
          const dateParts = session.date.match(/(\w+) (\d+), (\d+)/);
          if (dateParts) {
            const [_, month, day, year] = dateParts;
            sessionDate = new Date(`${month} ${day}, ${year}`);
          } else {
            // Fallback to current date
            sessionDate = new Date();
          }
        }
      } catch (e) {
        console.error("Error parsing date:", e);
        sessionDate = new Date();
      }
    } else {
      // Handle null, undefined, or other unexpected types
      console.warn("Unexpected date format, using current date:", session.date);
      sessionDate = new Date();
    }
    
    const formValues: TrainingFormValues = {
      sessionType: (session.title as SessionType) || 'Gi', // Map database title to sessionType
      date: sessionDate,
      duration: session.duration,
      professor: session.professor || '',
      techniques: Array.isArray(session.techniques) ? session.techniques.join(', ') : '',
      location: session.location || '',
      learned: session.learned || '',
      improvements: session.improvements || '',
      successes: session.successes || '',
      notes: session.notes || '',
      youtube_url: session.youtubeUrl || '',
      belt_rank: session.beltRank || 'white',
      stripe_count: session.stripeCount || '0',
      // Competition specific fields - map from database to form
      competition_total_matches: session.competitionTotalMatches || 0,
      competition_weight_classes: session.competitionWeightClasses || '',
      competition_total_wins: session.competitionTotalWins || 0,
      competition_finish: session.competitionFinish || '',
      competition_matches: session.competitionMatches || []
    };

    return formValues;
  } catch (error) {
    console.error("Error creating form values from session:", error);
    return null;
  }
};

export interface SessionFormValues {
  fromSession: typeof getFormValuesFromSession;
}

// Export the full object
export const SessionFormValues = {
  fromSession: getFormValuesFromSession
};

/**
 * Clears any draft form data from storage
 */
export const clearFormDraft = () => {
  try {
    localStorage.removeItem('training-form-draft');
  } catch (error) {
    console.error("Error clearing form draft:", error);
  }
};

/**
 * Ensures value is a proper Date object
 */
export const ensureDateObject = (dateValue: unknown): Date => {
  if (!dateValue) return new Date();
  
  const isDateObject = Object.prototype.toString.call(dateValue) === '[object Date]';
  if (isDateObject) return dateValue as Date;
  
  if (typeof dateValue === 'string') {
    try {
      return new Date(dateValue);
    } catch (e) {
      return new Date();
    }
  }
  
  return new Date();
};

/**
 * Gets default values for the form, incorporating any initial values provided
 */
export const getDefaultFormValues = (initialValues?: Partial<TrainingFormValues>): Partial<TrainingFormValues> => {
  const lastUsedProfessor = typeof window !== 'undefined' ? localStorage.getItem('last-used-professor') || '' : '';
  const homeGym = typeof window !== 'undefined' ? localStorage.getItem('user-home-gym') || '' : '';
  
  const defaults: Partial<TrainingFormValues> = {
    sessionType: 'Gi',
    date: new Date(),
    duration: 60,
    professor: lastUsedProfessor,
    techniques: '',
    location: homeGym,
    learned: '',
    improvements: '',
    successes: '',
    notes: ''
  };

  // If initialValues include a date string, convert it to Date object
  const processedInitialValues = initialValues ? { ...initialValues } : undefined;
  
  // Handle date conversion
  if (processedInitialValues && processedInitialValues.date) {
    // Check if date is already a Date object using type-safe approach
    const isDateObject = Object.prototype.toString.call(processedInitialValues.date) === '[object Date]';
    if (!isDateObject) {
      try {
        // Convert string to Date
        processedInitialValues.date = new Date(processedInitialValues.date as unknown as string);
      } catch (e) {
        console.error("Error converting date:", e);
        processedInitialValues.date = new Date();
      }
    }
  }

  return processedInitialValues ? { ...defaults, ...processedInitialValues } : defaults;
};
